<template>
  <div class="msp-power-profile">

      <div class="msp-power-profile__controls">
          <div v-if="!hasPasso" class="msp-power-profile__modo">
              <label class="msp-power-profile__radio">
                  <input type="radio" v-model="modo" value="watt"> W
              </label>
              <label class="msp-power-profile__radio">
                  <input type="radio" v-model="modo" value="watt_kg"> W/kg
              </label>
          </div>
          <button
              v-if="periodOptions.length || hasKjSeries"
              class="msp-power-profile__settings-btn"
              :class="{ 'msp-power-profile__settings-btn--open': settingsOpen }"
              @click="settingsOpen = !settingsOpen"
          >
              <i class="msp-icon-settings"></i> Impostazioni
          </button>
      </div>

      <div v-if="settingsOpen" class="msp-power-profile__settings-panel">
          <div v-if="periodOptions.length" class="msp-power-profile__settings-group">
              <div class="msp-power-profile__settings-title">Periodo</div>
              <label
                  v-for="p in periodOptions"
                  :key="p.key"
                  class="msp-power-profile__settings-item"
              >
                  <input type="checkbox" :checked="periodsVisible[p.key]" @change="togglePeriod(p.key)">
                  {{ p.label }}
              </label>
          </div>
          <div v-if="hasKjSeries" class="msp-power-profile__settings-group">
              <div class="msp-power-profile__settings-title">Soglie durability</div>
              <label class="msp-power-profile__settings-item msp-power-profile__settings-item--radio">
                  <input type="radio" v-model="activeSoglia" :value="null"> Nessuna
              </label>
              <label
                  v-for="opt in kjouleOptions"
                  :key="opt.kj"
                  class="msp-power-profile__settings-item msp-power-profile__settings-item--radio"
              >
                  <input type="radio" v-model="activeSoglia" :value="opt.kj"> {{ opt.label }}
              </label>
              <label class="msp-power-profile__settings-item msp-power-profile__settings-item--radio">
                  <input type="radio" v-model="activeSoglia" value="all"> Tutte le soglie
              </label>
          </div>
      </div>

      <div class="msp-power-profile__graph" ref="powerProfile"></div>

      <div v-if="tooltip.visible" class="msp-cp-tooltip">
          <div v-if="tooltip.title" class="msp-cp-tooltip__title">{{ tooltip.title }}</div>
          <table class="msp-cp-tooltip__table">
              <thead>
                  <tr class="msp-cp-tooltip__header-row">
                      <th class="msp-cp-tooltip__th msp-cp-tooltip__th--periodo">Periodo</th>
                      <th class="msp-cp-tooltip__th msp-cp-tooltip__th--mmp">MMP</th>
                      <th
                          v-for="g in tooltip.visibleKjGroups"
                          :key="g.start"
                          class="msp-cp-tooltip__th"
                      >{{ g.label }}</th>
                      <th v-if="activeSoglia && activeSoglia !== 'all'" class="msp-cp-tooltip__th msp-cp-tooltip__th--pct">%</th>
                  </tr>
              </thead>
              <tbody>
                  <tr v-for="row in tooltip.rows" :key="row.pi">
                      <td class="msp-cp-tooltip__cell msp-cp-tooltip__cell--periodo">{{ row.period }}</td>
                      <td class="msp-cp-tooltip__cell msp-cp-tooltip__cell--value">
                          <span v-if="row.mmpVal != null" class="msp-cp-tooltip__cell-inner">
                              <span class="msp-cp-dot" :style="{ background: row.mmpColor }"></span>
                              <span class="msp-cp-value">{{ row.mmpFormatted }}</span><small>{{ tooltip.um_cp }}</small>
                          </span>
                          <span v-else class="msp-cp-tooltip__cell--empty">-</span>
                      </td>
                      <template v-for="(kj, gi) in row.kjCells">
                          <td :key="'v' + row.pi + '-' + gi" class="msp-cp-tooltip__cell msp-cp-tooltip__cell--value">
                              <span v-if="kj.val != null" class="msp-cp-tooltip__cell-inner">
                                  <span class="msp-cp-dot" :style="{ background: kj.color }"></span>
                                  <span class="msp-cp-value">{{ kj.formatted }}</span><small>{{ tooltip.um_cp }}</small>
                              </span>
                              <span v-else class="msp-cp-tooltip__cell--empty">-</span>
                          </td>
                          <td v-if="activeSoglia && activeSoglia !== 'all'" :key="'p' + row.pi + '-' + gi" class="msp-cp-tooltip__cell msp-cp-tooltip__cell--pct">
                              {{ kj.pct }}
                          </td>
                      </template>
                  </tr>
              </tbody>
          </table>
      </div>

  </div>
</template>

<script>
import {
    Chart,
    LineController,
    LinearScale,
    LogarithmicScale,
    PointElement,
    LineElement,
} from 'chart.js';
import _api from '@mixins/api.js';
import { env } from '@mixins/environment.js';
import { SERIES_CONFIG, KJ_GROUPS, PERIOD_LABELS, VALORI_KEYS, makeData, formatY } from '@shared/attivita/powerProfile.js';

Chart.register(LineController, LinearScale, LogarithmicScale, PointElement, LineElement);

const stripHtml = (str) => str.replace(/<[^>]*>/g, '');

export default {
    name: 'MspPowerProfile',
    props: ['attivita_id'],

    data() {
        return {
            gruppoSport: null,
            valori: null,
            modo: 'watt',
            periodsVisible: { oggi: true, '3mesi': true, anno: true, alltime: true },
            activeSoglia: null,
            settingsOpen: false,
            tooltip: {
                visible: false,
                title: '',
                rows: [],
                visibleKjGroups: [],
                um_cp: '',
            },
        };
    },

    computed: {
        hasPasso() {
            return this.gruppoSport === 'PODISMO' || this.gruppoSport === 'NUOTO';
        },

        computedSeriesVisible() {
            return SERIES_CONFIG.map(cfg => {
                if (!this.periodsVisible[cfg.periodo]) return false;
                if (cfg.soglia_kj === 0) return true;
                if (this.activeSoglia === 'all') return true;
                return cfg.soglia_kj === this.activeSoglia;
            });
        },

        seriesWithData() {
            if (!this.valori) return [];
            return SERIES_CONFIG
                .map((cfg, i) => ({ ...cfg, idx: i, hasData: (this.valori[VALORI_KEYS[i]] ?? []).some(el => el.x && el.y) }))
                .filter(s => s.hasData);
        },

        periodOptions() {
            const seen = new Set();
            return this.seriesWithData
                .filter(s => { if (seen.has(s.periodo)) return false; seen.add(s.periodo); return true; })
                .map(s => ({ key: s.periodo, label: ['Oggi', '3 mesi', `${new Date().getFullYear()}`, 'All Time'][['oggi','3mesi','anno','alltime'].indexOf(s.periodo)] }));
        },

        kjouleOptions() {
            const seen = new Set();
            return this.seriesWithData
                .filter(s => s.soglia_kj > 0 && !seen.has(s.soglia_kj) && seen.add(s.soglia_kj))
                .map(s => ({ kj: s.soglia_kj, label: `dopo ${s.soglia_kj} kJ` }));
        },

        hasKjSeries() {
            return this.kjouleOptions.length > 0;
        },
    },

    watch: {
        modo() {
            if (this.valori) this.creaGrafico(this.valori, this.gruppoSport);
        },

        computedSeriesVisible(newVal) {
            if (!this._chart) return;
            newVal.forEach((visible, i) => {
                const dsIdx = this._chart.data.datasets.findIndex(d => d.seriesIdx === i);
                if (dsIdx >= 0) this._chart.setDatasetVisibility(dsIdx, visible);
            });
            this._chart.update();
            if (this._activeX != null) this.tooltip = this._buildTooltip(this._activeX);
        },

    },

    mounted() {
        this.makeGraph();
    },

    beforeDestroy() {
        this._destroyGrafico();
    },

    methods: {
        async loadPotenzaCritica() {
            if (!this.attivita_id) return null;
            const url = env.api_base_url + "/api/v2/attivita.php?power-profile&id=" + this.attivita_id;
            const result = await _api(url, 'get');
            if (!result.success) return null;
            return result.data;
        },

        makeGraph() {
            this.loadPotenzaCritica().then((data) => {
                if (!data) {
                    this.$emit('has-data', false);
                    return;
                }
                this.gruppoSport = data.sport;
                this.valori = data.valori;
                if (data.valori) this.creaGrafico(data.valori, data.sport);
                this.$emit('has-data', this.seriesWithData.length > 0);
            });
        },

        togglePeriod(key) {
            this.$set(this.periodsVisible, key, !this.periodsVisible[key]);
        },

        _buildTooltip(x) {
            const sport = this.gruppoSport;
            const modo = this.modo;
            const um_cp = sport === 'PODISMO' ? '/km' : (modo === 'watt_kg' ? ' W/kg' : ' W');
            const showUnits = window.PPU !== false;
            const chart = this._chart;
            const lstRifLineCP = this._lstRifLineCP;

            const valBySeries = {};
            chart.data.datasets.forEach((ds, dsIdx) => {
                if (!chart.isDatasetVisible(dsIdx)) return;
                const point = ds.data.find(p => p.x === x);
                if (!point || point.y == null) return;
                valBySeries[ds.seriesIdx] = point.y;
            });

            const rifLine = lstRifLineCP?.find(l => l.value === x);
            const baseTitle = rifLine ? stripHtml(rifLine.label.text.trim()) : '';
            const title = showUnits ? baseTitle : `${baseTitle} ${um_cp.trim()}`;

            const visibleKjGroups = KJ_GROUPS.filter(g =>
                [0, 1, 2, 3].some(pi => valBySeries[g.start + pi] != null)
            );

            const rows = [];
            for (let pi = 0; pi < 4; pi++) {
                const mmpVal = valBySeries[pi] ?? null;
                const showPct = this.activeSoglia !== null && this.activeSoglia !== 'all';
                const kjCells = visibleKjGroups.map((g) => {
                    const v = valBySeries[g.start + pi] ?? null;
                    let pct = '';
                    if (showPct && pi === 0 && mmpVal != null && mmpVal !== 0 && v != null) {
                        const n = sport === 'PODISMO'
                            ? Math.round(mmpVal / v * 100)
                            : Math.round(v / mmpVal * 100);
                        pct = `${n}%`;
                    }
                    return {
                        val: v,
                        formatted: v != null ? formatY(v, sport, modo) : null,
                        color: v != null ? SERIES_CONFIG[g.start + pi].color : null,
                        pct,
                    };
                });

                if (mmpVal == null && kjCells.every(c => c.val == null)) continue;

                rows.push({
                    pi,
                    period: PERIOD_LABELS[pi],
                    mmpVal,
                    mmpFormatted: mmpVal != null ? formatY(mmpVal, sport, modo) : null,
                    mmpColor: SERIES_CONFIG[pi].color,
                    kjCells,
                });
            }

            return { visible: true, title, rows, visibleKjGroups, um_cp: showUnits ? um_cp : '' };
        },

        creaGrafico(valori, sport) {
            this._destroyGrafico();

            const targetDiv = this.$refs.powerProfile;
            const modo = this.modo;
            const lstRifLineCP = valori.CPRiferimento;
            const isReversed = sport === 'PODISMO';
            const seriesVisible = this.computedSeriesVisible;

            this._lstRifLineCP = lstRifLineCP;
            this._activeX = null;

            const rawDatasets = VALORI_KEYS.map(k => makeData(valori[k] ?? [], sport, modo));
            const xValues = Array.from(new Set(rawDatasets.flat().map(p => p.x))).sort((a, b) => a - b);
            const xMin = xValues[0] ?? undefined;
            const xMax = xValues[xValues.length - 1] ?? undefined;
            const alignedDatasets = rawDatasets.map(data =>
                xValues.map(x => ({ x, y: data.find(p => p.x === x)?.y ?? null }))
            );

            const datasets = SERIES_CONFIG
                .map((cfg, i) => {
                    const data = alignedDatasets[i];
                    if (!data || data.every(p => p.y == null)) return null;
                    return {
                        label: cfg.label,
                        seriesIdx: i,
                        data,
                        borderColor: cfg.color,
                        backgroundColor: cfg.color,
                        borderDash: cfg.dash,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        tension: 0,
                        spanGaps: false,
                        hidden: seriesVisible ? !seriesVisible[i] : false,
                    };
                })
                .filter(Boolean);

            const crosshairPlugin = {
                id: 'cp-crosshair',
                afterDraw: (chart) => {
                    if (this._activeX == null) return;
                    const xPixel = chart.scales.x.getPixelForValue(this._activeX);
                    const { ctx, chartArea: { top, bottom } } = chart;

                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(xPixel, top);
                    ctx.lineTo(xPixel, bottom);
                    ctx.strokeStyle = 'rgba(80, 80, 80, 0.55)';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                    ctx.restore();

                    chart.data.datasets.forEach((ds, dsIdx) => {
                        if (!chart.isDatasetVisible(dsIdx)) return;
                        const point = ds.data.find(p => p.x === this._activeX);
                        if (!point || point.y == null) return;
                        const yPixel = chart.scales.y.getPixelForValue(point.y);
                        ctx.save();
                        ctx.beginPath();
                        ctx.arc(xPixel, yPixel, 5, 0, Math.PI * 2);
                        ctx.fillStyle = ds.borderColor;
                        ctx.fill();
                        ctx.strokeStyle = '#fff';
                        ctx.lineWidth = 1.5;
                        ctx.stroke();
                        ctx.restore();
                    });
                },
            };

            const plotlinesPlugin = {
                id: 'cp-plotlines',
                beforeDatasetsDraw(chart) {
                    if (!lstRifLineCP?.length) return;
                    const { ctx, scales: { x: xScale, y: yScale } } = chart;
                    ctx.font = '10px sans-serif';

                    const lines = lstRifLineCP
                        .map(line => {
                            const xPixel = xScale.getPixelForValue(line.value);
                            if (xPixel < xScale.left || xPixel > xScale.right) return null;
                            const text = line.label?.text ? stripHtml(line.label.text.trim()) : null;
                            const halfWidth = text ? ctx.measureText(text).width / 2 : 0;
                            return { line, xPixel, text, halfWidth };
                        })
                        .filter(Boolean);

                    const LABEL_GAP = 4;
                    let lastLabelRight = -Infinity;
                    const showLabel = lines.map(({ xPixel, halfWidth }) => {
                        const labelLeft = xPixel - halfWidth;
                        if (labelLeft >= lastLabelRight + LABEL_GAP) {
                            lastLabelRight = xPixel + halfWidth;
                            return true;
                        }
                        return false;
                    });

                    lines.forEach(({ line, xPixel, text }, i) => {
                        ctx.save();
                        ctx.beginPath();
                        ctx.moveTo(xPixel, yScale.top);
                        ctx.lineTo(xPixel, yScale.bottom);
                        ctx.strokeStyle = line.color || '#aaaaaa';
                        ctx.lineWidth = line.width || 1;
                        if (line.dashStyle === 'Dash' || line.dashStyle === 'ShortDash') {
                            ctx.setLineDash([4, 4]);
                        }
                        ctx.stroke();
                        if (showLabel[i] && text) {
                            ctx.fillStyle = '#555555';
                            ctx.font = '10px sans-serif';
                            ctx.textAlign = 'center';
                            ctx.fillText(text, xPixel, yScale.bottom + 12);
                        }
                        ctx.restore();
                    });
                },
            };

            const canvas = document.createElement('canvas');
            targetDiv.innerHTML = '';
            targetDiv.appendChild(canvas);

            const chart = new Chart(canvas, {
                type: 'line',
                plugins: [crosshairPlugin, plotlinesPlugin],
                data: { datasets },
                options: {
                    clip: false,
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: false,
                    layout: {
                        padding: { left: 8, right: 8, bottom: 14 },
                    },
                    scales: {
                        x: {
                            type: 'logarithmic',
                            min: xMin,
                            max: xMax,
                            grid: { display: false },
                            ticks: { display: false },
                            border: { display: true },
                        },
                        y: {
                            reverse: isReversed,
                            grid: { display: false },
                            ticks: { callback: (value) => formatY(value, sport, modo) ?? '' },
                        },
                    },
                    plugins: {
                        tooltip: { enabled: false },
                        legend: { display: false },
                    },
                },
            });

            this._chart = chart;

            const getNearestX = (clientX) => {
                const rect = canvas.getBoundingClientRect();
                const pixelX = clientX - rect.left;
                const xScale = chart.scales.x;
                if (pixelX < xScale.left || pixelX > xScale.right) return null;
                const logVal = xScale.getValueForPixel(pixelX);
                return xValues.reduce((a, b) =>
                    Math.abs(b - logVal) < Math.abs(a - logVal) ? b : a
                );
            };

            const showTooltip = (clientX) => {
                const x = getNearestX(clientX);
                if (x == null) return;
                this._activeX = x;
                this.tooltip = this._buildTooltip(x);
                chart.update('none');
            };

            const hideTooltip = () => {
                this._activeX = null;
                this.tooltip = { visible: false, title: '', rows: [], visibleKjGroups: [], um_cp: '' };
                chart.update('none');
            };

            let lastTouchTime = 0;
            const isSyntheticMouse = () => Date.now() - lastTouchTime < 300;

            this._onMouseMove  = (e) => { if (!isSyntheticMouse()) showTooltip(e.clientX); };
            this._onMouseLeave = ()  => { if (!isSyntheticMouse()) hideTooltip(); };
            this._onTouchStart = (e) => { lastTouchTime = Date.now(); showTooltip(e.touches[0].clientX); };
            this._onTouchMove  = (e) => { lastTouchTime = Date.now(); showTooltip(e.touches[0].clientX); };
            this._onTouchEnd   = (e) => { lastTouchTime = Date.now(); showTooltip(e.changedTouches[0].clientX); };

            canvas.addEventListener('mousemove',  this._onMouseMove);
            canvas.addEventListener('mouseleave', this._onMouseLeave);
            canvas.addEventListener('touchstart', this._onTouchStart, { passive: true });
            canvas.addEventListener('touchmove',  this._onTouchMove,  { passive: true });
            canvas.addEventListener('touchend',   this._onTouchEnd,   { passive: true });

            this._canvas = canvas;
        },

        _destroyGrafico() {
            if (this._canvas) {
                if (this._onMouseMove)  this._canvas.removeEventListener('mousemove',  this._onMouseMove);
                if (this._onMouseLeave) this._canvas.removeEventListener('mouseleave', this._onMouseLeave);
                if (this._onTouchStart) this._canvas.removeEventListener('touchstart', this._onTouchStart);
                if (this._onTouchMove)  this._canvas.removeEventListener('touchmove',  this._onTouchMove);
                if (this._onTouchEnd)   this._canvas.removeEventListener('touchend',   this._onTouchEnd);
                this._canvas = null;
            }
            if (this._chart) {
                this._chart.destroy();
                this._chart = null;
            }
        },
    },
};
</script>

<style scoped>
.msp-power-profile__controls {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 4px;
    font-size: 0.9em;
}

.msp-power-profile__modo {
    display: flex;
    gap: 8px;
}

.msp-power-profile__radio {
    display: flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
}

.msp-power-profile__settings-btn {
    margin-left: auto;
    background: none;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 3px 10px;
    cursor: pointer;
    font-size: inherit;
    display: flex;
    align-items: center;
    gap: 5px;
}

.msp-power-profile__settings-btn--open {
    border-color: #888;
    background: #f5f5f5;
}

.msp-power-profile__settings-panel {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px 4px;
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;
    margin-bottom: 4px;
    font-size: 0.85em;
}

.msp-power-profile__settings-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.msp-power-profile__settings-title {
    font-weight: 600;
    color: #555;
    margin-bottom: 2px;
}

.msp-power-profile__settings-item {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
}

.msp-power-profile__graph {
    height: 280px;
}

.msp-cp-tooltip {
    padding: 8px 4px;
    font-size: 12px;
    color: #333;
}

.msp-cp-tooltip__title {
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 5px;
}

.msp-cp-tooltip__table {
    border-collapse: collapse;
    width: 100%;
}

.msp-cp-tooltip__header-row {
    border-bottom: 1px solid #ddd;
    color: #888;
}

.msp-cp-tooltip__th {
    padding: 2px 6px;
    text-align: left;
    font-weight: normal;
}

.msp-cp-tooltip__th--periodo {
    padding-left: 0;
}

.msp-cp-tooltip__th--mmp {
    text-align: left;
}

.msp-cp-tooltip__cell {
    padding: 2px 6px;
    vertical-align: middle;
}

.msp-cp-tooltip__cell--periodo {
    padding-left: 0;
}

.msp-cp-tooltip__cell--value {
    font-weight: normal;
}

.msp-cp-tooltip__cell--empty {
    color: #aaa;
}

.msp-cp-tooltip__cell-inner {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.msp-cp-value {
    font-weight: bold;
}

.msp-cp-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}
</style>
